/**
 * User edit modal component for admin user management.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { UserListResponse, UserUpdateRequest, updateUser } from '../api/users';
import './UserEditModal.css';

interface UserEditModalProps {
  user: UserListResponse;
  currentUserId?: number;
  onClose: () => void;
  onSave: (userId: number, updates: UserUpdateRequest) => Promise<void>;
}

export const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  currentUserId,
  onClose,
  onSave,
}) => {
  const isCurrentUser = user.id === currentUserId;
  const [email, setEmail] = useState(user.email);
  const [name, setName] = useState(user.name);
  const [isActive, setIsActive] = useState(user.is_active);
  const [role, setRole] = useState<string>(user.is_admin ? 'admin' : 'user');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEmail(user.email);
    setName(user.name);
    setIsActive(user.is_active);
    setRole(user.is_admin ? 'admin' : 'user');
    setError(null);
  }, [user]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const updates: UserUpdateRequest = {};
      
      if (email !== user.email) {
        updates.email = email;
      }
      if (name !== user.name) {
        updates.name = name;
      }
      // Prevent users from deactivating themselves
      if (isCurrentUser && !isActive && user.is_active) {
        setError('You cannot deactivate your own account');
        setIsLoading(false);
        return;
      }
      
      if (isActive !== user.is_active) {
        updates.is_active = isActive;
      }
      
      // Prevent users from removing their own admin status
      const isAdmin = role === 'admin';
      if (isCurrentUser && user.is_admin && !isAdmin) {
        setError('You cannot remove your own administrator privileges');
        setIsLoading(false);
        return;
      }
      
      if (isAdmin !== user.is_admin) {
        updates.is_admin = isAdmin;
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setIsLoading(false);
        return;
      }

      // Call API directly and handle errors in modal
      try {
        await updateUser(user.id, updates);
        // On success, notify parent to refresh and close modal
        setIsLoading(false);
        onSave(user.id, updates);
        onClose();
        return;
      } catch (err: any) {
        // Handle error in modal - don't propagate to parent
        let errorMessage = 'Failed to update user. Please try again.';
        if (err?.response?.data) {
          const errorData = err.response.data;
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
    } catch (err: any) {
      // Catch any other errors
      let errorMessage = 'Failed to update user. Please try again.';
      if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit User</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="edit-email">Email</label>
            <input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              required
              disabled={isLoading}
              placeholder="Enter email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-name">Full Name</label>
            <input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              required
              disabled={isLoading}
              placeholder="Enter full name"
              minLength={1}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-status">Status</label>
            <div className="toggle-switch-container">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => {
                    setIsActive(e.target.checked);
                    setError(null);
                  }}
                  disabled={isLoading || (isCurrentUser && user.is_active)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">{isActive ? 'Active' : 'Inactive'}</span>
            </div>
            <small className="form-hint">
              {isCurrentUser && user.is_active 
                ? 'You cannot deactivate your own account' 
                : 'Inactive users cannot log in'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="edit-role">Role</label>
            <select
              id="edit-role"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setError(null);
              }}
              disabled={isLoading || (isCurrentUser && user.is_admin)}
              className="select-input"
            >
              <option value="user">User</option>
              <option value="admin">Administrator</option>
              {/* Add more roles here as they are implemented */}
            </select>
            <small className="form-hint">
              {isCurrentUser && user.is_admin
                ? 'You cannot remove your own administrator privileges'
                : 'Administrators have full access'}
            </small>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

